import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { Pro, ProIndex, DeckPro, PagedResult, ProSearchRequest, SubjectAggregate } from '../../models';

export interface IProService {

	page(pageIndex: number, pageSize: number): Promise<PagedResult<Pro>>;
	get(id: string): Promise<Pro>;
	getMultiple(ids: string[]): Promise<Pro[]>;
	autocomplete(crtieria: string): Promise<Pro[]>;
	index(): Promise<ProIndex[]>;
	search(request: ProSearchRequest): Promise<PagedResult<Pro>>;
	save(model: Pro): Promise<Pro>;
	delete(id: string): Promise<any>;
	listByCompanyWithCount(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Pro>>>;
	pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckPro>>;
	listByDeck(deckId: string): Promise<DeckPro[]>;
	listByCompany(companyId: string): Promise<Pro[]>;
	pageByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<Pro>>;

}

@injectable()
export class ProService implements IProService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	public page(pageIndex: number, pageSize: number): Promise<PagedResult<Pro>> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Pro>>(url);
	}

	public get(id: string): Promise<Pro> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/${id}`;
		return this.httpClient.get<Pro>(url);
	}

	public getMultiple(ids: string[]): Promise<Pro[]> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Pro[]>(url);
	}

	public autocomplete(criteria: string): Promise<Pro[]> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/autocomplete/${criteria}`;
		return this.httpClient.get<Pro[]>(url);
	}

	public index(): Promise<ProIndex[]> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/index`;
		return this.httpClient.get<ProIndex[]>(url);
	}

	public search(request: ProSearchRequest): Promise<PagedResult<Pro>> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/search`;
		return this.httpClient.post<PagedResult<Pro>>(url, request);
	}


	public save(model: Pro): Promise<Pro> {
		const payload: { [k: string]: any } = {
			displayName: model.displayName,
			firstName: model.firstName,
			middleName: model.middleName,
			lastName: model.lastName,
			nickName: model.nickName,
			birthDate: model.birthDate,
			birthYear: (model.birthYear != null) ? parseInt(model.birthYear.toString(), null) : null,
			birthMonth: (model.birthMonth != null) ? parseInt(model.birthMonth.toString(), null) : null,
			birthDay: (model.birthDay != null) ? parseInt(model.birthDay.toString(), null) : null,
			deathYear: (model.deathYear != null) ? parseInt(model.deathYear.toString(), null) : null,
			deathMonth: (model.deathMonth != null) ? parseInt(model.deathMonth.toString(), null) : null,
			deathDay: (model.deathDay != null) ? parseInt(model.deathDay.toString(), null) : null,
			yearTurnedPro: (model.yearTurnedPro != null) ? parseInt(model.yearTurnedPro.toString(), null) : null,
			aosId: (model.aosId != null) ? parseInt(model.aosId.toString(), null) : null,
			description: model.description,
			genderId: (model.genderId != null) ? parseInt(model.genderId.toString(), null) : 0,
			stanceId: (model.stanceId != null) ? parseInt(model.stanceId.toString(), null) : 0,
			hometown: model.hometown,
			isEnabled: model.isEnabled,
		};
		let url = `${this.settings.baseUrl}${this.settings.proBaseUrl}`;
		if (model.id !== null && model.id !== undefined && model.id !== '') {
			url += `/${model.id}`;
			return this.httpClient.put(url, payload);
		} else {
			return this.httpClient.post(url, payload);
		}
	}

	public delete(id: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/${id}`;
		return this.httpClient.delete(url);
	}

	public listByDeck(deckId: string): Promise<DeckPro[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/pros`;
		return this.httpClient.get<DeckPro[]>(url);
	}

	public pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckPro>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/pros/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckPro>>(url);
	}

	public listByCompany(companyId: string): Promise<Pro[]> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/pros`;
		return this.httpClient.get<Pro[]>(url);
	}

	public listByCompanyWithCount(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Pro>>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/pros/with-count/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<SubjectAggregate<Pro>>>(url);
	}

	public pageByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<Pro>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/pros/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Pro>>(url);
	}



}
